// Shared API client for the web SaaS. Talks to the same backend as the
// Figma plugin (api.snapsiteux.com) — CORS is already wide open so a
// cross-origin call from snapsiteux.com works untouched.

import type {
  AnalyzeResult,
  CapturedPage,
  HeuristicFinding,
  AccessibilityFinding,
  JobState as PluginJobState,
  Viewport,
  RunMode,
} from "./audit-types";

export const API_BASE =
  process.env.NEXT_PUBLIC_SNAPSITE_API_BASE ?? "https://api.snapsiteux.com/api";

export type BillingPlan = "free" | "pro" | "agency";

export interface AuthenticatedUser {
  id: string;
  email: string;
  plan: BillingPlan;
  subscriptionStatus: string;
}

export interface Entitlements {
  plan: BillingPlan;
  subscriptionStatus: string;
  credits: {
    balance: number;
    monthlyGrant: number;
    /** Recurring credits left this period (free + subscription). Optional
        for back-compat with API responses predating the split. */
    monthly?: number;
    /** One-time top-up credits left (purchased bucket). */
    topup?: number;
  };
  features: {
    mobile: boolean;
    bothViewports: boolean;
    aiInteractions: boolean;
    maxPagesPerRun: number;
  };
  limits: { devices: number; concurrentJobs: number };
}

export interface JobSummary {
  id: string;
  sourceUrl: string;
  status: PluginJobState["status"];
  runMode?: RunMode;
  viewports: Viewport[];
  origin?: "plugin" | "web";
  pageCount: number;
  thumbnailUrl?: string;
  siteName?: string;
  score?: number;
  /** Total UX + accessibility findings across all captured pages. */
  findingsCount?: number;
  /** Completed run where every page came back unrendered (blank / blocked /
      404) — the distinct "Couldn't capture" state, auto-refunded. Still
      openable (unlike a hard Failed). */
  captureFailed?: boolean;
  createdAt: number;
  updatedAt: number;
  error?: string;
  /** User-set display name; falls back to siteName / hostname. */
  displayName?: string | null;
  /** Containing folder id; null = root. */
  folderId?: string | null;
  /** Trashed-at timestamp; only set for items in the trash. */
  trashedAt?: number | null;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string; detail?: string };
    return data.error ?? data.detail ?? fallback;
  } catch {
    return fallback;
  }
}

function authHeaders(token?: string | null): HeadersInit {
  return token ? { authorization: `Bearer ${token}` } : {};
}

// ---------- Auth ----------

export async function startMagicCode(email: string, clientId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/start`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, clientId }),
  });
  if (!res.ok) {
    throw new ApiError(await readError(res, "We could not send a code."), res.status);
  }
}

export interface VerifyResponse {
  token: string;
  user: AuthenticatedUser;
  entitlements: Entitlements;
}

export async function verifyMagicCode(opts: {
  email: string;
  code: string;
  clientId: string;
}): Promise<VerifyResponse> {
  const res = await fetch(`${API_BASE}/auth/verify`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  if (!res.ok) {
    throw new ApiError(await readError(res, "That code did not work."), res.status);
  }
  return (await res.json()) as VerifyResponse;
}

// Sign in with Google. `credential` is the GIS ID token returned by the
// "Continue with Google" button. The backend verifies it, auto-merges onto an
// existing account by email if one exists, and returns the same session
// payload as the magic-code flow — so the caller stores auth identically.
export async function verifyGoogleSignIn(opts: {
  credential: string;
  clientId: string;
}): Promise<VerifyResponse> {
  const res = await fetch(`${API_BASE}/auth/google`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  if (!res.ok) {
    throw new ApiError(await readError(res, "Google sign-in didn't work."), res.status);
  }
  return (await res.json()) as VerifyResponse;
}

export async function fetchEntitlements(token: string): Promise<{
  user: AuthenticatedUser;
  entitlements: Entitlements;
}> {
  const res = await fetch(`${API_BASE}/entitlements`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not load your account."),
      res.status
    );
  }
  return (await res.json()) as {
    user: AuthenticatedUser;
    entitlements: Entitlements;
  };
}

// ---------- Audits ----------

export interface StartAnalyzeInput {
  url: string;
  runMode: RunMode;
  viewports: Viewport[];
  highlights?: boolean;
  heuristicsAudit?: boolean;
  notes?: boolean;
  maxPages?: number;
  aiNote?: string;
  clientId: string;
}

export async function startAnalyze(
  token: string,
  input: StartAnalyzeInput
): Promise<{ jobId: string; status: string }> {
  const res = await fetch(`${API_BASE}/analyze`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({ ...input, origin: "web" }),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not start the audit."),
      res.status
    );
  }
  return (await res.json()) as { jobId: string; status: string };
}

// ---------- Guest audits (run-before-login conversion path) ----------

export interface StartGuestAuditInput {
  url: string;
  clientId: string;
}

// Starts an ownerless two-page, desktop-only audit with NO auth token. The
// backend mints a per-job `guestToken` (the read + claim secret) and returns
// it here. Caller stores { jobId, guestToken } locally and navigates to
// /try/[jobId]. See app/lib/guest-audit.ts.
export async function startGuestAudit(
  input: StartGuestAuditInput
): Promise<{ jobId: string; status: string; guestToken?: string }> {
  const res = await fetch(`${API_BASE}/analyze`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      url: input.url,
      runMode: "audit",
      viewports: ["desktop"],
      heuristicsAudit: true,
      notes: true,
      highlights: false,
      maxPages: 2,
      clientId: input.clientId,
      origin: "web",
    }),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not start the audit."),
      res.status
    );
  }
  return (await res.json()) as {
    jobId: string;
    status: string;
    guestToken?: string;
  };
}

// Assigns a guest audit to the now-signed-in user. Requires the auth token AND
// the per-job guest secret. Idempotent on the server.
export async function claimGuestAudit(
  token: string,
  jobId: string,
  guestToken: string
): Promise<{ ok: true; jobId: string }> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/claim`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...authHeaders(token),
        "x-guest-token": guestToken,
      },
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not save this audit to your account."),
      res.status
    );
  }
  return (await res.json()) as { ok: true; jobId: string };
}

export interface StartImageAuditInput {
  file: File;
  clientId: string;
}

export async function startImageAudit(
  token: string,
  input: StartImageAuditInput
): Promise<{ jobId: string; status: string }> {
  const { file, clientId } = input;
  const headers: Record<string, string> = {
    "content-type": file.type,
    "x-snap-original-name": encodeURIComponent(file.name),
  };
  if (clientId) headers["x-snap-client-id"] = clientId;
  const auth = authHeaders(token);
  Object.assign(headers, auth);

  const arrayBuffer = await file.arrayBuffer();
  const res = await fetch(`${API_BASE}/jobs/image`, {
    method: "POST",
    headers,
    body: arrayBuffer,
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not start the image audit."),
      res.status
    );
  }
  return (await res.json()) as { jobId: string; status: string };
}

export async function fetchJob(
  jobId: string,
  token?: string | null,
  guestToken?: string | null
): Promise<PluginJobState> {
  // Send auth when available so the backend's ownership gate can identify
  // the requester — without it, the GET /jobs/:id handler can't tell the
  // owner from an anonymous reader and 403s the owner's own audit when
  // their plan doesn't include share links.
  //
  // For guest audits (run before login) there's no token yet — the per-job
  // guest secret authorizes the read instead, via the x-guest-token header.
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (guestToken) headers["x-guest-token"] = guestToken;
  const res = await fetch(`${API_BASE}/jobs/${encodeURIComponent(jobId)}`, {
    headers,
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not load that audit."),
      res.status
    );
  }
  return (await res.json()) as PluginJobState;
}

// Opt a failed audit into a background retry (the "we'll try again in ~15 min
// and email you" path). Sends the owner's token or the guest token so the
// backend can attribute the job, plus the address to notify on completion.
export async function scheduleRetry(
  jobId: string,
  email: string,
  token?: string | null,
  guestToken?: string | null
): Promise<{ ok: boolean; retryAt: number | null }> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;
  if (guestToken) headers["x-guest-token"] = guestToken;
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/schedule-retry`,
    { method: "POST", headers, body: JSON.stringify({ email }) }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Couldn't schedule a retry."),
      res.status
    );
  }
  return (await res.json()) as { ok: boolean; retryAt: number | null };
}

// Authorizes a canvas PDF export. The Pro/Agency gate is keyed to the audit
// owner's plan on the server; after it passes, the browser captures the live
// canvas it is already displaying. Public share viewers of a Pro-owned audit
// pass the same access check without needing the owner's token.
export async function authorizeAuditPdfExport(
  jobId: string,
  token?: string | null,
  guestToken?: string | null,
  signal?: AbortSignal
): Promise<void> {
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (guestToken) headers["x-guest-token"] = guestToken;
  const res = await fetch(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/export`, {
    method: "POST",
    headers,
    signal,
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not export this audit as a PDF."),
      res.status
    );
  }
}

export async function deleteJob(token: string, jobId: string): Promise<void> {
  // Try DELETE /jobs/:id first (the canonical RESTful path). If that
  // 405s/404s OR the fetch itself throws — most commonly a CORS preflight
  // failure when an older deploy of the API hasn't included DELETE in
  // its allow-methods yet — fall back to the dedicated
  // /jobs/delete?id=… adapter. Either path satisfies the same handler.
  let direct: Response | null = null;
  try {
    direct = await fetch(`${API_BASE}/jobs/${encodeURIComponent(jobId)}`, {
      method: "DELETE",
      headers: authHeaders(token),
    });
  } catch {
    direct = null;
  }
  if (direct?.ok) return;
  if (
    direct &&
    direct.status !== 404 &&
    direct.status !== 405 &&
    direct.status !== 0
  ) {
    throw new ApiError(
      await readError(direct, "Could not delete this audit."),
      direct.status
    );
  }
  let fallback: Response;
  try {
    fallback = await fetch(
      `${API_BASE}/jobs/delete?id=${encodeURIComponent(jobId)}`,
      { method: "DELETE", headers: authHeaders(token) }
    );
  } catch (err) {
    throw new ApiError(
      err instanceof Error
        ? `Network error — ${err.message}. The delete endpoint may not be deployed yet.`
        : "Could not reach the server.",
      0
    );
  }
  if (!fallback.ok) {
    throw new ApiError(
      await readError(fallback, "Could not delete this audit."),
      fallback.status
    );
  }
}

export async function listMyJobs(token: string, limit = 30): Promise<JobSummary[]> {
  const res = await fetch(`${API_BASE}/jobs/mine?limit=${limit}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not load your audits."),
      res.status
    );
  }
  const body = (await res.json()) as { jobs: JobSummary[] };
  return body.jobs;
}

// ---------- In-product feedback (first-audit dialog) ----------

export type FeedbackRating = "great" | "okay" | "bad";

export async function submitAuditFeedback(
  token: string,
  input: { rating: FeedbackRating; message?: string; jobId?: string }
): Promise<void> {
  const res = await fetch(`${API_BASE}/feedback`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...authHeaders(token),
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not send your feedback. Try again."),
      res.status
    );
  }
}

// ---------- Audit layout (drag-to-position card overrides) ----------

export interface AuditLayout {
  jobId: string;
  cardOffsets: Record<string, { x: number; y: number }>;
  /** Per-finding dot center overrides, in % (0-100) of the shot. */
  pinOverrides: Record<string, { x: number; y: number }>;
  bookmarked: string[];
  solved: string[];
  updatedAt: number;
}

export interface AuditLayoutPatch {
  cardOffsets?: Record<string, { x: number; y: number }>;
  pinOverrides?: Record<string, { x: number; y: number }>;
  bookmarked?: string[];
  solved?: string[];
}

function emptyAuditLayout(jobId: string): AuditLayout {
  return {
    jobId,
    cardOffsets: {},
    pinOverrides: {},
    bookmarked: [],
    solved: [],
    updatedAt: 0,
  };
}

export async function fetchAuditLayout(
  jobId: string,
  token?: string | null
): Promise<AuditLayout> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/layout`,
    { headers: { ...authHeaders(token) } }
  );
  if (!res.ok) {
    if (res.status === 404 || res.status === 403) {
      return emptyAuditLayout(jobId);
    }
    throw new ApiError(
      await readError(res, "Could not load layout."),
      res.status
    );
  }
  const body = (await res.json()) as { layout: AuditLayout };
  // Older backends might omit the new fields — coerce to safe defaults.
  return {
    jobId: body.layout.jobId,
    cardOffsets: body.layout.cardOffsets ?? {},
    pinOverrides: body.layout.pinOverrides ?? {},
    bookmarked: body.layout.bookmarked ?? [],
    solved: body.layout.solved ?? [],
    updatedAt: body.layout.updatedAt ?? 0,
  };
}

export async function saveAuditLayout(
  token: string,
  jobId: string,
  patch: AuditLayoutPatch
): Promise<AuditLayout> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/layout`,
    {
      method: "PUT",
      headers: { "content-type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(patch),
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not save layout."),
      res.status
    );
  }
  const body = (await res.json()) as { layout: AuditLayout };
  return body.layout;
}

// ---------- Custom annotations ----------

export interface CustomAnnotation {
  id: string;
  jobId: string;
  authorUserId: string | null;
  pageIndex: number;
  viewport: Viewport;
  title: string;
  severity: "high" | "medium" | "low";
  description: string;
  bbox: { x: number; y: number; width: number; height: number };
  createdAt: number;
  updatedAt: number;
}

export interface CustomAnnotationDraft {
  pageIndex: number;
  viewport: Viewport;
  title: string;
  severity: "high" | "medium" | "low";
  description?: string;
  bbox: { x: number; y: number; width: number; height: number };
}

export async function listCustomAnnotations(
  jobId: string,
  token?: string | null
): Promise<CustomAnnotation[]> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/annotations`,
    { headers: { ...authHeaders(token) } }
  );
  if (!res.ok) {
    if (res.status === 404 || res.status === 403) return [];
    throw new ApiError(
      await readError(res, "Could not load annotations."),
      res.status
    );
  }
  const body = (await res.json()) as { annotations: CustomAnnotation[] };
  return body.annotations ?? [];
}

export async function createCustomAnnotation(
  token: string,
  jobId: string,
  draft: CustomAnnotationDraft
): Promise<CustomAnnotation> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/annotations`,
    {
      method: "POST",
      headers: { "content-type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(draft),
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not save annotation."),
      res.status
    );
  }
  const body = (await res.json()) as { annotation: CustomAnnotation };
  return body.annotation;
}

export async function updateCustomAnnotation(
  token: string,
  jobId: string,
  annoId: string,
  patch: Partial<CustomAnnotationDraft>
): Promise<CustomAnnotation> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/annotations/${encodeURIComponent(annoId)}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(patch),
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not update annotation."),
      res.status
    );
  }
  const body = (await res.json()) as { annotation: CustomAnnotation };
  return body.annotation;
}

export async function deleteCustomAnnotation(
  token: string,
  jobId: string,
  annoId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/annotations/${encodeURIComponent(annoId)}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not delete annotation."),
      res.status
    );
  }
}

// ---------- Folders + trash ----------

export interface Folder {
  id: string;
  parentId: string | null;
  name: string;
  trashedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export async function listFolders(token: string): Promise<Folder[]> {
  const res = await fetch(`${API_BASE}/folders`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    if (res.status === 404) return [];
    throw new ApiError(
      await readError(res, "Could not load folders."),
      res.status
    );
  }
  const body = (await res.json()) as { folders: Folder[] };
  return body.folders ?? [];
}

export async function createFolder(
  token: string,
  input: { name: string; parentId: string | null }
): Promise<Folder> {
  const res = await fetch(`${API_BASE}/folders`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not create folder."),
      res.status
    );
  }
  const body = (await res.json()) as { folder: Folder };
  return body.folder;
}

export async function updateFolder(
  token: string,
  id: string,
  patch: { name?: string; parentId?: string | null }
): Promise<Folder> {
  const res = await fetch(`${API_BASE}/folders/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not update folder."),
      res.status
    );
  }
  const body = (await res.json()) as { folder: Folder };
  return body.folder;
}

export async function deleteFolder(token: string, id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/folders/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not delete folder."),
      res.status
    );
  }
}

export async function updateJobMetadata(
  token: string,
  jobId: string,
  patch: { displayName?: string | null; folderId?: string | null }
): Promise<JobSummary> {
  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(patch),
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not update audit."),
      res.status
    );
  }
  const body = (await res.json()) as { job: JobSummary };
  return body.job;
}

export interface TrashContents {
  folders: Folder[];
  jobs: JobSummary[];
}

export async function listTrash(token: string): Promise<TrashContents> {
  const res = await fetch(`${API_BASE}/trash`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not load trash."),
      res.status
    );
  }
  return (await res.json()) as TrashContents;
}

export async function restoreFromTrash(
  token: string,
  type: "job" | "folder",
  id: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/trash/${type}/${encodeURIComponent(id)}`,
    { method: "POST", headers: authHeaders(token) }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not restore."),
      res.status
    );
  }
}

export async function purgeFromTrash(
  token: string,
  type: "job" | "folder",
  id: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/trash/${type}/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: authHeaders(token) }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not delete forever."),
      res.status
    );
  }
}

// ---------- Append page to an existing audit ----------

export interface AppendPageResponse {
  page: CapturedPage;
  result: AnalyzeResult;
}

export async function appendPageToAudit(
  token: string,
  jobId: string,
  url: string,
  // Optional per-page viewport override. When omitted, the backend
  // falls back to the audit's default viewports — preserves the
  // pre-existing API contract for callers that haven't been updated.
  viewports?: ReadonlyArray<"desktop" | "mobile">
): Promise<AppendPageResponse> {
  const body: { url: string; viewports?: ReadonlyArray<"desktop" | "mobile"> } = {
    url,
  };
  if (viewports && viewports.length > 0) body.viewports = viewports;

  const res = await fetch(
    `${API_BASE}/jobs/${encodeURIComponent(jobId)}/append-page`,
    {
      method: "POST",
      headers: { "content-type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not add this page to the audit."),
      res.status
    );
  }
  return (await res.json()) as AppendPageResponse;
}

// ---------- Public demo gallery ----------

// Curated, publicly-viewable demo audits for the no-signup homepage gallery.
// No auth; returns [] on any error so the homepage degrades gracefully (the
// section just doesn't render).
export async function fetchDemos(): Promise<JobSummary[]> {
  try {
    const res = await fetch(`${API_BASE}/demos`);
    if (!res.ok) return [];
    const body = (await res.json()) as { demos?: JobSummary[] };
    return body.demos ?? [];
  } catch {
    return [];
  }
}

// Public site-wide audit count for the homepage social-proof counter. No
// auth; returns null on any error so the counter can hide rather than show
// a wrong/zero number. The real count comes from completed, non-demo audits.
export async function fetchPublicStats(): Promise<{ audits: number } | null> {
  try {
    const res = await fetch(`${API_BASE}/stats`);
    if (!res.ok) return null;
    const body = (await res.json()) as { audits?: number };
    return typeof body.audits === "number" ? { audits: body.audits } : null;
  } catch {
    return null;
  }
}

// ---------- Persistent usage summary ----------

export interface UsagePeriod {
  credits: number;
  pages: number;
  runs: number;
}

export interface UsageSummary {
  monthly: UsagePeriod;
  lastMonth: UsagePeriod;
  /** Credits spent per day, oldest→newest. Length 30; index 29 = today. */
  trend: number[];
  /**
   * Lifetime activity totals, persistent across deletion: an audit moved to
   * Trash (a soft delete) still counts here, so the dashboard's "Your
   * activity" numbers don't shrink when projects are deleted. Optional for
   * backward compatibility with older backend responses.
   */
  lifetime?: {
    audits: number;
    findings: number;
    auditsLast30d: number;
  };
  /** Recent credit-ledger entries (grants, charges, refunds, top-ups),
   *  newest first. Optional for older backend responses. */
  creditEvents?: Array<{ at: string; credits: number; type: string }>;
}

export async function fetchUsageSummary(
  token: string
): Promise<UsageSummary> {
  const res = await fetch(`${API_BASE}/usage/summary`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) {
    throw new ApiError(
      await readError(res, "Could not load usage."),
      res.status
    );
  }
  const body = (await res.json()) as { summary: UsageSummary };
  return body.summary;
}

export type { AnalyzeResult, CapturedPage, HeuristicFinding, AccessibilityFinding };
